import React from 'react';
import { css } from 'glamor';

import { StateDot } from '../../components/bayAccess/StateDot';
import { styles } from './styles';

export function deviceDescriptor(bay) {
    if (bay.reachability === 'OFFLINE') return { flavour: 'danger', text: 'Offline' };
    if (bay.reachability === 'BLIND') return { flavour: 'danger', text: 'Gateway offline' };
    if (bay.reachability === 'MISSING') return { flavour: 'gray', text: 'No device' };
    if (bay.reachability === 'UNKNOWN') return { flavour: 'gray', text: 'Telemetry unavailable' };
    if (bay.position === 'OPEN') return { flavour: 'primary', text: 'Accessible' };
    if (bay.position === 'CLOSED') return { flavour: 'success', text: 'Protected' };
    if (bay.position === 'JAMMED') return { flavour: 'danger', text: 'Jammed' };
    return { flavour: 'gray', text: 'Unknown' };
}

function batteryTier(percent) {
    if (percent < 20) return styles.invBatteryLow;
    if (percent < 50) return styles.invBatteryWarn;
    return styles.invBatteryGood;
}

// Every booking overlapping a cell [cellStart, cellEnd) (open-ended → +Infinity).
// A coarse (Week/Month daily) cell can hold several same-day bookings.
function bookingsForCell(bookings, cellStart, cellEnd) {
    const result = [];
    for (let index = 0; index < bookings.length; index += 1) {
        const booking = bookings[index];
        const lo = booking.lo ? new Date(booking.lo).getTime() : -Infinity;
        const hi = booking.hi ? new Date(booking.hi).getTime() : Infinity;
        if (lo < cellEnd && hi > cellStart) result.push(booking);
    }
    return result;
}

// An overstaying booking whose overstay tail [hi, now) overlaps a cell, or null.
// The tail represents the car still present past departure (the booking block ended at hi).
function overstayForCell(bookings, cellStart, cellEnd, now) {
    for (let index = 0; index < bookings.length; index += 1) {
        const booking = bookings[index];
        if (booking.status !== 'overstaying' || !booking.hi) continue;
        const hi = new Date(booking.hi).getTime();
        if (hi < now && hi < cellEnd && now > cellStart) return booking;
    }
    return null;
}

// Button semantics + Enter/Space activation for a clickable booking cell (a11y).
function cellInteraction(onActivate, label) {
    return {
        role: 'button',
        tabIndex: 0,
        'aria-label': label,
        onClick: onActivate,
        onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onActivate();
            }
        },
    };
}

function Row({ bay, bookings, cells, now, showNow, nowFraction, canManage, onMenu, onOpenBooking }) {
    const outOfService = bay.status === 'unavailable';
    const device = outOfService ? { flavour: 'gray', text: 'Out of service' } : deviceDescriptor(bay);
    const attention = !outOfService && bay.reachability !== 'ONLINE';
    return (
        <div className={css(styles.invRow, attention && styles.invRowAttention)}>
            <div className={css(styles.invBayCell, attention && styles.invBayAttentionBar)}>
                <div>
                    <div className={styles.invBayLabel}>{bay.label}</div>
                    <div className={styles.invDevice}>
                        <StateDot flavour={device.flavour}>{`Parklio · ${device.text}`}</StateDot>
                    </div>
                    {bay.batteryPercent != null ? (
                        <div className={css(styles.invBattery, batteryTier(bay.batteryPercent))}>
                            {`Battery ${bay.batteryPercent}%`}
                        </div>
                    ) : null}
                </div>
                {canManage ? (
                    <button type="button" className={styles.kebab} onClick={(event) => onMenu(bay, event.currentTarget)}>⋯</button>
                ) : null}
            </div>
            <div className={css(styles.invCells, outOfService && styles.invCellsOos)}>
                {showNow ? <div className={styles.invNow} style={{ left: `${nowFraction * 100}%` }} /> : null}
                {outOfService && bookings.length === 0 ? <span className={styles.oosLabel}>Out of service</span> : null}
                {cells.map((cell, index) => {
                    const cellBookings = bookingsForCell(bookings, cell.start, cell.end);
                    const overstay = overstayForCell(bookings, cell.start, cell.end, now);
                    if (cellBookings.length > 1) {
                        return (
                            <div key={cell.start} className={styles.slotCell} style={{ flex: 1, display: 'flex' }}>
                                {cellBookings.map((item) => (
                                    <div
                                        key={item.bookingId}
                                        className={css(styles.slotAllocated, styles.slotAllocatedStart)}
                                        style={{ flex: 1, cursor: 'pointer', overflow: 'hidden' }}
                                        title={`${item.plate}${item.guestName ? ` · ${item.guestName}` : ''}`}
                                        {...cellInteraction(() => onOpenBooking(item, bay), `Open booking for ${item.plate}`)}
                                    >
                                        <span className={styles.slotLabel}>{item.plate}</span>
                                    </div>
                                ))}
                            </div>
                        );
                    }
                    const booking = cellBookings[0] || null;
                    const previous = index > 0 ? (bookingsForCell(bookings, cells[index - 1].start, cells[index - 1].end)[0] || null) : null;
                    const isStart = booking && (!previous || previous.bookingId !== booking.bookingId);
                    const active = booking || overstay;
                    return (
                        <div
                            key={cell.start}
                            style={{ flex: 1, cursor: active ? 'pointer' : undefined }}
                            className={css(styles.slotCell, booking && styles.slotAllocated, isStart && styles.slotAllocatedStart, overstay && styles.slotOverstay)}
                            title={active ? `${active.plate}${active.guestName ? ` · ${active.guestName}` : ''}${overstay ? ' · overstaying' : ''}` : undefined}
                            {...(active ? cellInteraction(() => onOpenBooking(active, bay), `Open booking for ${active.plate}`) : {})}
                        >
                            {isStart ? <span className={styles.slotLabel}>{booking.plate}</span> : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export function BayInventory({ bays, timelineByBay, cells, groups, start, windowMs, now, canManage, onMenu, onOpenBooking }) {
    const currentCellIndex = cells.findIndex((cell) => now >= cell.start && now < cell.end);
    const showNow = currentCellIndex >= 0;
    const currentCell = showNow ? cells[currentCellIndex] : null;
    const nowFraction = currentCell
        ? (currentCellIndex + (now - currentCell.start) / (currentCell.end - currentCell.start)) / cells.length
        : 0;

    const zones = [];
    const byZone = Object.create(null);
    bays.forEach((bay) => {
        const zone = bay.zone || 'Bays';
        if (!byZone[zone]) {
            byZone[zone] = [];
            zones.push(zone);
        }
        byZone[zone].push(bay);
    });

    return (
        <div className={styles.invWrap}>
            <div className={styles.invAxis}>
                <div className={styles.invAxisBay}>Bay</div>
                <div className={styles.invAxisRight}>
                    <div className={styles.invGroupRow}>
                        {groups.map((group) => (
                            <div key={group.key} style={{ flex: group.span }} className={styles.invGroupTick}>{group.label}</div>
                        ))}
                    </div>
                    <div className={styles.invTickRow}>
                        {cells.map((cell) => (
                            <div key={cell.start} style={{ flex: 1 }} className={styles.invAxisTick}>{cell.label}</div>
                        ))}
                    </div>
                </div>
            </div>
            {zones.map((zone) => (
                <div key={zone}>
                    <div className={styles.invGroup}>{zone}</div>
                    {byZone[zone].map((bay) => (
                        <Row
                            key={bay.id}
                            bay={bay}
                            bookings={timelineByBay[bay.id] || []}
                            cells={cells}
                            now={now}
                            showNow={showNow}
                            nowFraction={nowFraction}
                            canManage={canManage}
                            onMenu={onMenu}
                            onOpenBooking={onOpenBooking}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
}
